import { DateHelper } from "@/Common/Helpers/DateHelper";
import { DateOnly } from "@/Common/Utilities/DateAndTime/DateOnly";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { WorkSafeBcPhysicianReportModel } from "./WorkSafeBcPhysicianReportModel";
import { WorkSafeBcWorkerModel } from "./WorkSafeBcWorkerModel";

class WorkSafeBcFormValidator {
    private WorkSafeBcForm: WorkSafeBcPhysicianReportModel;
    private Today: Date;
    private WorkSafeBcWorkerModel: WorkSafeBcWorkerModel;

    constructor(workSafeBcForm: WorkSafeBcPhysicianReportModel, workSafeBcWorkerModel: WorkSafeBcWorkerModel = null) {
        this.WorkSafeBcForm = workSafeBcForm;
        this.Today = DateOnly.today().toJSDate();
        this.WorkSafeBcWorkerModel = workSafeBcWorkerModel;
    }

    public isFormValidForClaim(): boolean {
        return (
            this.isServiceDateValid()
            && this.isInjuryOrFirstTreatmentForConditionDateValid()
            && this.isIsWorkerRegularPractitionerValid()
            && this.isWorkerRegularPractitionerTimeSpanValid()
            && this.isHasWorkerBeenDisabledFromLastReportOrInjuryValid()
            && this.isWorkerDisabledDateValid()
            && this.isAreaOfInjuryCodeValid()
            && this.isNatureOfInjuryCodeValid()
            && this.isDiagnosticCodeValid()
            && this.isIsWorkerMedicallyCapableToReturnToWorkValid()
            && this.isDurationOfWorkReturnValid()
            && this.isIsWorkerReadyForRehabValid()
            && this.isRehabilitationProgramValid()
            && this.isConsultWorkSafeBcPhysicianOrNurseAdvisorValid()
            && this.isEmployerNameValid()
            && this.isDiagnosisTextValid()
            && this.isPriorProblemsAffectingRecoveryFieldValid()
            && this.isClinicalInformationFieldValid()
            && this.isAnatomicalPositionValid()
            && this.isOperatingLocationAddressValid()
            && this.isWorkSafeBcWorkerModelValid()
        );
    }

    /*
    * Validation regarding claims
    */
    public isServiceDateValid(): boolean {
        return (
            DateHelper.isValidDate(this.WorkSafeBcForm.ServiceDate, null, null, false)
            && DateOnly.parseUnlessNull(this.WorkSafeBcForm.ServiceDate).toLuxon() <= LuxonDateTimeParser.parse(this.Today)
        );
    }

    public isInjuryOrFirstTreatmentForConditionDateValid(): boolean {
        const injuryDate = this.WorkSafeBcForm?.InjuryOrFirstTreatmentForConditionDate;

        return (
            !!injuryDate
            && (
                !this.WorkSafeBcForm?.ServiceDate
                || DateOnly.parseUnlessNull(injuryDate).toLuxon() <= LuxonDateTimeParser.parse(this.WorkSafeBcForm?.ServiceDate)
            )
        );
    }

    public isIsWorkerRegularPractitionerValid(): boolean {
        return (
            !!this.WorkSafeBcForm.IsWorkerRegularPractitioner
            || this.WorkSafeBcForm.IsWorkerRegularPractitioner === false
        );
    }

    public isWorkerRegularPractitionerTimeSpanValid(): boolean {
        return (
            !this.WorkSafeBcForm.IsWorkerRegularPractitioner
            || this.WorkSafeBcForm.WorkerRegularPractitionerTimeSpan > 0
        );
    }

    public isHasWorkerBeenDisabledFromLastReportOrInjuryValid(): boolean {
        return (
            !!this.WorkSafeBcForm.HasWorkerBeenDisabledFromLastReportOrInjury
            || this.WorkSafeBcForm.HasWorkerBeenDisabledFromLastReportOrInjury === false
        );
    }

    public isWorkerDisabledDateValid(): boolean {
        const workerDisabledDate = this.WorkSafeBcForm.WorkerDisabledDate;

        return (
            !this.WorkSafeBcForm.HasWorkerBeenDisabledFromLastReportOrInjury
            || (
                DateHelper.isValidDate(workerDisabledDate, null, null, false)
                && (
                    !this.WorkSafeBcForm?.ServiceDate
                    || DateOnly.parseUnlessNull(workerDisabledDate).toLuxon() <= LuxonDateTimeParser.parse(this.WorkSafeBcForm?.ServiceDate)
                )
            )
        );
    }

    public isAreaOfInjuryCodeValid(): boolean {
        return !IsNullOrWhiteSpace(this.WorkSafeBcForm.AreaOfInjuryCode);
    }

    public isNatureOfInjuryCodeValid(): boolean {
        return !IsNullOrWhiteSpace(this.WorkSafeBcForm.NatureOfInjuryCode);
    }

    public isDiagnosticCodeValid(): boolean {
        return !IsNullOrWhiteSpace(this.WorkSafeBcForm.DiagnosticCode);
    }

    public isIsWorkerMedicallyCapableToReturnToWorkValid(): boolean {
        return (
            !!this.WorkSafeBcForm.IsWorkerMedicallyCapableToReturnToWork
            || this.WorkSafeBcForm.IsWorkerMedicallyCapableToReturnToWork === false
        );
    }

    public isDurationOfWorkReturnValid(): boolean {
        return this.WorkSafeBcForm.DurationOfWorkReturn > 0;
    }

    public isIsWorkerReadyForRehabValid(): boolean {
        return (
            !!this.WorkSafeBcForm.IsWorkerReadyForRehab
            || this.WorkSafeBcForm.IsWorkerReadyForRehab === false
        );
    }

    public isRehabilitationProgramValid(): boolean {
        return (
            !this.WorkSafeBcForm.IsWorkerReadyForRehab
            || this.WorkSafeBcForm.RehabilitationProgram > 0
        );
    }

    public isConsultWorkSafeBcPhysicianOrNurseAdvisorValid(): boolean {
        return (
            !!this.WorkSafeBcForm.ConsultWorkSafeBcPhysicianOrNurseAdvisor
            || this.WorkSafeBcForm.ConsultWorkSafeBcPhysicianOrNurseAdvisor === false
        );
    }

    public isEmployerNameValid(): boolean {
        return !IsNullOrWhiteSpace(this.WorkSafeBcForm.EmployerName);
    }

    public isDiagnosisTextValid(): boolean {
        return !IsNullOrWhiteSpace(this.WorkSafeBcForm.Diagnosis);
    }

    public isPriorProblemsAffectingRecoveryFieldValid(): boolean {
        return !IsNullOrWhiteSpace(this.WorkSafeBcForm.PriorProblemsAffectingRecovery);
    }

    public isClinicalInformationFieldValid(): boolean {
        return !IsNullOrWhiteSpace(this.WorkSafeBcForm.ClinicalInformation);
    }

    public isAnatomicalPositionValid(): boolean {
        return !!this.WorkSafeBcForm.AnatomicalPosition;
    }

    public isOperatingLocationAddressValid(): boolean {
        return !IsNullOrWhiteSpace(this.WorkSafeBcForm.OperatingLocationAddress?.City);
    }

    public isWorkSafeBcWorkerModelValid(): boolean {
        return this.isWorkerFirstNameValid()
            && this.isWorkerLastNameValid()
            && this.isWorkerGenderValid()
            && this.isWorkerDateofBirthValid()
            && this.isWorkerHealthCardNumberValid()
            && this.isWorkerMailingAddressValid();
    }

    /*
     * WorkSafeBcWorkerModel Validation
     */
    public isWorkerFirstNameValid(): boolean {
        return !IsNullOrWhiteSpace(this.WorkSafeBcWorkerModel?.FirstName);
    }

    public isWorkerLastNameValid(): boolean {
        return !IsNullOrWhiteSpace(this.WorkSafeBcWorkerModel?.LastName);
    }

    public isWorkerGenderValid(): boolean {
        return !IsNullOrWhiteSpace(this.WorkSafeBcWorkerModel?.Gender);
    }

    public isWorkerDateofBirthValid(): boolean {
        return DateHelper.isValidDate(this.WorkSafeBcWorkerModel?.BirthDate, null, null, false);
    }

    public isWorkerHealthCardNumberValid(): boolean {
        return !IsNullOrWhiteSpace(this.WorkSafeBcWorkerModel?.HealthCardNumber);
    }

    public isWorkerMailingAddressValid(): boolean {
        return !IsNullOrWhiteSpace(this.WorkSafeBcWorkerModel?.MailingAddress?.StreetAddress)
            && !IsNullOrWhiteSpace(this.WorkSafeBcWorkerModel?.MailingAddress?.City)
            && !IsNullOrWhiteSpace(this.WorkSafeBcWorkerModel?.MailingAddress?.ZipCode);
    }

    /*
    * Other validation
    */
    public isCurrentPhysicalOrPsychologicalRestrictionsValid(): boolean {
        return (
            this.WorkSafeBcForm.IsWorkerMedicallyCapableToReturnToWork !== false
            || !IsNullOrWhiteSpace(this.WorkSafeBcForm.CurrentPhysicalOrPsychologicalRestrictions)
        );
    }
}

export { WorkSafeBcFormValidator };
